import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { sendNotificationSchema, type SendNotificationForm } from '../schemas';

/**
 * Send Notification dialog (Task B). Real submit: POST /admin/api/moderation/send-notification via
 * `submitNotification` (useModerationActions). User Detail uses the same endpoint via SystemNotificationDialog.
 * Title/message stay disabled from submit until both are non-blank.
 */
export function SendNotificationDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (title: string, message: string) => Promise<void>;
  isSubmitting: boolean;
}) {
  const form = useForm<SendNotificationForm>({
    resolver: zodResolver(sendNotificationSchema),
    defaultValues: { title: '', message: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) form.reset();
  }, [open, form]);

  const submit = form.handleSubmit(async (values) => {
    await onSubmit(values.title, values.message);
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) form.reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send Notification</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void submit(e)} className="space-y-3">
          <div className="space-y-1">
            <Input placeholder="Title" {...form.register('title')} />
            {form.formState.errors.title && (
              <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Textarea rows={5} placeholder="Write a notification…" {...form.register('message')} />
            {form.formState.errors.message && (
              <p className="text-xs text-destructive">{form.formState.errors.message.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!form.formState.isValid || isSubmitting}>
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
